<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'

export type AvatarSize = 'sm' | 'md' | 'lg'

interface Props {
  /**
   * Stable identifier used to pick a palette slot. The same id always
   * yields the same color pair — useful for "Ahmed is always indigo".
   * If absent, the avatar falls back to the first palette slot.
   */
  id?: string | number | null
  /**
   * Display name — initials are derived from the first letter of each
   * whitespace-separated chunk (first 2 only). Falls back to "??" if empty.
   * Examples: "Ahmed Khan" → "AK", "Reema" → "R", "" → "??".
   */
  name?: string | null
  /** Explicit initials override — bypasses derivation from `name`. */
  initials?: string
  /** Size preset. Default `md` (40px). */
  size?: AvatarSize
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
})

// Decorative palette — six bg/fg pairs cycled per id. Intentionally
// kept inside the kit (not state tokens) — these are identity tags,
// not status surfaces, so 6 distinct hues is the right cardinality.
const PALETTES = [
  { bg: '#EDE9FE', color: '#7C3AED' }, // violet
  { bg: '#DBEAFE', color: '#2563EB' }, // blue
  { bg: '#DCFCE7', color: '#16A34A' }, // green
  { bg: '#FEF3C7', color: '#B45309' }, // amber
  { bg: '#FCE7F3', color: '#BE185D' }, // rose
  { bg: '#CCFBF1', color: '#0D9488' }, // teal
] as const

const palette = computed(() => {
  const seed = props.id == null ? '' : String(props.id)
  if (!seed) return PALETTES[0]
  const code = seed.charCodeAt(seed.length - 1)
  return PALETTES[code % PALETTES.length] ?? PALETTES[0]
})

const derivedInitials = computed(() => {
  if (props.initials) return props.initials.slice(0, 2).toUpperCase()
  const name = (props.name ?? '').trim()
  if (!name) return '??'
  const parts = name.split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const second = parts[1]?.[0] ?? ''
  return (first + second).toUpperCase() || '??'
})
</script>

<template>
  <Box
    class="avatar"
    :class="[`avatar--${size}`]"
    :style="{ background: palette?.bg, color: palette?.color }"
  >
    <Text
      :type="size === 'sm' ? 'caption' : 'body-sm'"
      weight="bold"
      :style="{ color: palette?.color }"
    >
      {{ derivedInitials }}
    </Text>
  </Box>
</template>

<style scoped>
.avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;
  user-select: none;
}

.avatar--sm {
  width: 32px;
  height: 32px;
}
.avatar--md {
  width: 40px;
  height: 40px;
}
.avatar--lg {
  width: 56px;
  height: 56px;
}
</style>
